import React, { ElementType, SyntheticEvent, useState } from 'react';

import { useLocation, useParams } from 'react-router';
import { Link } from '~/lib/react-router-dom';

import { Box, Stack, Tab, Tabs } from '@mui/material';

import PageSubheader from '../Common/Elements/PageSubheader';
import { titleCase, useRegisterSpotlightRoutes } from '../Common/Spotlight/SpotlightProvider';

type Props = {
  items: Array<{
    title: string;
    component: ElementType;
    slug?: string;
    componentProps?: Record<string, unknown>;
  }>;
  defaultItem: string;
  sort?: boolean;
};

export default function ContentWithSidebar({ items, defaultItem, sort = true }: Props) {
  const { section, report = defaultItem } = useParams<{
    section: string;
    report?: string;
  }>();
  const { pathname } = useLocation();

  // Derive base path by stripping /:section and anything after it from the current URL.
  // e.g. "/finance/bank-files/citi-dom-file" → basePath="/finance", section="bank-files"
  const sectionStart = pathname.lastIndexOf(`/${section}`);
  const basePath = sectionStart >= 0 ? pathname.substring(0, sectionStart) : '';
  const parentLabel = titleCase(basePath.split('/').filter(Boolean).pop() ?? '');
  const tabLabel = titleCase(section);

  useRegisterSpotlightRoutes(
    items.map((item) => ({
      path: `${basePath}/${section}/${item.slug ?? item.title}`,
      label: `${parentLabel} / ${tabLabel} / ${item.title}`,
      section: parentLabel,
    })),
  );

  const [current, setCurrent] = useState(report);

  const currentItem = items.find((item) => (item.slug ? item.slug === current : item.title === current));

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setCurrent(newValue);
  };

  const tabs = items.map((item) => item.title);
  if (sort) tabs.sort();

  return (
    <Stack direction="row" spacing={4}>
      <Tabs
        orientation="vertical"
        value={current}
        onChange={handleChange}
        sx={{ borderRight: 1, borderColor: 'divider' }}
      >
        {tabs.map((tabTitle, index) => {
          const tab = items.find((item) => item.title === tabTitle);
          return (
            <Tab
              key={tab.slug ?? index}
              label={tab.title}
              value={tab.slug ?? tab.title}
              sx={{
                alignItems: 'start',
                color: 'text.secondary',
                textAlign: 'left',
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              }}
              {...(tab.slug ? { component: Link, to: `/finance/${section}/${tab.slug}` } : {})}
            />
          );
        })}
      </Tabs>

      <Box flex={1}>
        {currentItem && (
          <>
            <PageSubheader title={currentItem.title} />
            <Box component={currentItem.component} {...(currentItem.componentProps ?? {})} />
          </>
        )}
      </Box>
    </Stack>
  );
}
